/* eslint-disable no-console */
import React, { memo, useState } from 'react';
import cn from 'classnames';
import { useFormContext, useFieldArray } from 'react-hook-form';
import SlideShow from 'components/SlideShow';
import EventCard from 'components/EventCard';
import TeamAndEventModal from 'components/TeamAndEventModal';
import { Button } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import {
  FlagshipEventAttributes,
  FlagshipEventRequest,
} from 'lib/models/flagship-event';
import { InsightRequest } from 'lib/models/insight';

import styles from './EventsV2.module.scss';

export interface EventsProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const EventsV2: React.FC<EventsProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: EventsProps) => {
    const contentClassNames = cn(
      {
        [styles.Events]: true,
      },
      className
    );
    const [isEditEvent, setIsEditEvent] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const [isEventModalOpen, setIsEventModalOpen] = useState(false);

    const { control } = useFormContext();
    const { fields, remove, prepend, append } = useFieldArray({
      control,
      name: 'flagship_events_attributes',
    });

    function isFileObject(object?: File) {
      return (
        object &&
        typeof object === 'object' &&
        'lastModified' in object &&
        'name' in object &&
        'size' in object &&
        'type' in object
      );
    }

    const eventFields = fields as Array<FlagshipEventAttributes>;
    const onAddEvent = ({
      icon,
      defaultImage,
      icon_url,
      ...data
    }: FlagshipEventRequest | InsightRequest | Record<string, never>) => {
      const imageurl = `${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/images/${defaultImage}`;
      const payload = {
        ...data,
        ...(!isFileObject(icon as File) && defaultImage
          ? { icon_url: icon_url || imageurl }
          : {}),
        ...(icon && isFileObject(icon as File) ? { icon } : {}),
      };
      if (isEditEvent && typeof selectedIndex === 'number') {
        remove(selectedIndex);
        prepend({ ...payload });
      } else {
        append({ ...payload, id: null });
      }
      setIsEventModalOpen(false);
      setIsEditEvent(false);
      setSelectedIndex(null);
    };

    const getImageSrc = (file: Blob | MediaSource) =>
      file instanceof File && URL.createObjectURL(file);
    return (
      <div className={contentClassNames} style={style} data-testid="Events">
        <main>
          {eventFields?.length ? (
            <SlideShow
              slidesPerRow={1}
              partialSlide={0.25}
              className={styles.EventsSlideShow}
              gutter={32}
              maxWidth="340px"
              isSocietyProfile={true}
              width={340}
            >
              {eventFields.map((event, index) => (
                <div className={styles.EventsSlideShowElement} key={index}>
                  <EventCard
                    className={styles.EventsSlideShowCard}
                    title={event.title}
                    image={
                      event?.icon
                        ? typeof event?.icon === 'object'
                          ? getImageSrc(event?.icon) || ''
                          : event?.icon
                        : event.icon_url
                        ? event.icon_url
                        : `${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/images/huzzle-gradient-20.jpg`
                    }
                    url={event.url}
                  />
                  <div className={styles.EventsSlideShowElementEditActions}>
                    <Button
                      className={styles.EventsSlideShowElementEdit}
                      color="white"
                      size="small"
                      borderless
                      onClick={() => {
                        setIsEditEvent(true);
                        setSelectedIndex(index);
                        setIsEventModalOpen(true);
                      }}
                      type="button"
                    >
                      Edit
                    </Button>
                    <Button
                      color="danger"
                      size="small"
                      borderless
                      onClick={() => remove(index)}
                      type="button"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </SlideShow>
          ) : null}
          <ButtonV3
            onClick={() => {
              setIsEditEvent(false);
              setSelectedIndex(null);
              setIsEventModalOpen(true);
            }}
            className={cn(styles.addButton, {
              [styles.addButtonSmall]: eventFields?.length,
            })}
            startIcon="plus"
            variant="contained"
            color="secondary"
            type="button"
          >
            Add flagship event
          </ButtonV3>
        </main>
        {isEventModalOpen ? (
          <TeamAndEventModal
            isOpen={isEventModalOpen}
            onCancel={() => setIsEventModalOpen(false)}
            onConfirm={(formData: FlagshipEventRequest | InsightRequest) =>
              onAddEvent(formData)
            }
            title={isEditEvent ? 'Edit flagship event' : 'Add flagship event'}
            onRemove={(index) => remove(index)}
            isEdit={isEditEvent}
            index={selectedIndex}
            defaultValues={
              isEditEvent && typeof selectedIndex === 'number'
                ? eventFields?.[selectedIndex]
                : {}
            }
          />
        ) : null}
      </div>
    );
  }
);

EventsV2.displayName = 'EventsV2';
